<template>
  <div class="permission-view">
    <v-toolbar
      flat
      class="permission-toolbar"
    >
      <v-btn
        icon
        class="mr-2"
        @click="goBack"
      >
        <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
      </v-btn>
      <div class="permission-toolbar__info">
        <div class="permission-toolbar__title">
          <v-icon
            small
            class="mr-2"
          >
            {{ icons.mdiShieldKey }}
          </v-icon>
          <span>{{ roleLabel }}</span>
        </div>
        <div class="permission-toolbar__subtitle">
          <v-chip
            small
            color="primary"
            text-color="white"
            class="mr-2"
          >
            {{ totalActive }} / {{ totalPermissions }} permissões ativas
          </v-chip>
          <span
            v-if="role && role.description"
            class="grey--text text--darken-1"
          >{{ role.description }}</span>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        text
        color="primary"
        class="mr-2"
        :disabled="saving || !unsavedChanges"
        @click="resetChanges"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiRestore }}
        </v-icon>
        Reverter alterações
      </v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="saving || !unsavedChanges"
        @click="save"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiContentSave }}
        </v-icon>
        Salvar alterações
      </v-btn>
    </v-toolbar>

    <v-alert
      v-if="unsavedChanges"
      type="warning"
      dense
      outlined
      class="mb-4"
    >
      Há alterações não salvas. Clique em "Salvar alterações" para aplicar ou "Reverter" para descartar.
    </v-alert>

    <v-card class="mb-4">
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="search"
              outlined
              dense
              hide-details
              :append-icon="icons.mdiMagnify"
              label="Pesquisar permissões"
              placeholder="Buscar por nome ou ação"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="filter-controls"
          >
            <div class="filter-label text-body-2 grey--text text--darken-1 mb-1">
              Filtrar por módulo
            </div>
            <v-chip-group
              v-model="selectedGroup"
              row
              mandatory
              active-class="primary--text"
              show-arrows
              class="filter-chip-group"
            >
              <v-chip
                v-for="option in groupOptions"
                :key="option.value"
                :value="option.value"
                outlined
                small
              >
                {{ option.label }}
                <span class="chip-count">{{ option.count }}</span>
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-skeleton-loader
      v-if="loading"
      type="card, card, card"
    ></v-skeleton-loader>

    <div v-else>
      <v-alert
        v-if="!filteredGroups.length"
        type="info"
        outlined
      >
        Nenhuma permissão encontrada para os filtros atuais.
      </v-alert>

      <v-expansion-panels
        v-model="expandedPanels"
        multiple
        class="permission-panels"
      >
        <v-expansion-panel
          v-for="group in filteredGroups"
          :key="group.key"
          :value="group.key"
        >
          <v-expansion-panel-header>
            <div class="panel-header">
              <div>
                <div class="panel-header__title">
                  {{ group.title }}
                </div>
                <div class="panel-header__subtitle">
                  {{ group.activeCount }} de {{ group.total }} permissões ativas
                </div>
              </div>
            </div>
            <template v-slot:actions>
              <div class="d-flex align-center panel-actions">
                <span class="panel-actions__label mr-2 text-caption grey--text">Ativar todas</span>
                <v-switch
                  :input-value="group.allActive"
                  color="primary"
                  hide-details
                  @click.stop
                  @change="onToggleGroup(group, $event)"
                ></v-switch>
                <v-icon
                  small
                  class="panel-arrow"
                  :class="{ 'panel-arrow--open': isPanelExpanded(group.key) }"
                >
                  {{ icons.mdiChevronDown }}
                </v-icon>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-divider class="mb-3"></v-divider>
            <v-row dense>
              <v-col
                v-for="permission in group.permissions"
                :key="permission.id"
                cols="12"
              >
                <div class="permission-item">
                  <div class="permission-item__info">
                    <div class="permission-item__title">
                      {{ permission.description }}
                    </div>
                  </div>
                  <v-switch
                    :input-value="permission.active"
                    color="primary"
                    hide-details
                    inset
                    @click.stop
                    @change="onTogglePermission(group.key, permission, $event)"
                  ></v-switch>
                </div>
                <v-divider class="my-3"></v-divider>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import {
  mdiArrowLeft,
  mdiChevronDown,
  mdiContentSave,
  mdiRestore,
  mdiMagnify,
  mdiShieldKey,
} from '@mdi/js'

export default {
  data: () => ({
    loading: false,
    saving: false,
    permissions: [],
    permissions_role: [],
    originalPermissions: [],
    role: null,
    search: '',
    selectedGroup: 'all',
    expandedPanels: [],
    unsavedChanges: false,
    icons: {
      mdiArrowLeft,
      mdiChevronDown,
      mdiContentSave,
      mdiRestore,
      mdiMagnify,
      mdiShieldKey,
    },
  }),
  computed: {
    totalPermissions() {
      return this.permissions.length
    },
    totalActive() {
      return this.permissions.filter(permission => permission.active).length
    },
    roleLabel() {
      if (this.role && this.role.name) {
        return `Perfil: ${this.role.name}`
      }

      return 'Permissões do Perfil'
    },
    groupOptions() {
      const counts = this.permissions.reduce((acc, permission) => {
        acc[permission.group] = (acc[permission.group] || 0) + 1

        return acc
      }, {})

      const options = [
        {
          value: 'all',
          label: 'Todos',
          count: this.totalPermissions,
        },
      ]

      Object.keys(counts)
        .sort()
        .forEach(group => {
          options.push({
            value: group,
            label: this.formatGroupTitle(group),
            count: counts[group],
          })
        })

      return options
    },
    filteredGroups() {
      const term = this.search.trim().toLowerCase()
      const groupsMap = {}

      this.permissions.forEach(permission => {
        const matchesSearch = !term
          || permission.description.toLowerCase().includes(term)

        const matchesGroup = this.selectedGroup === 'all' || permission.group === this.selectedGroup

        if (matchesSearch && matchesGroup) {
          if (!groupsMap[permission.group]) {
            groupsMap[permission.group] = []
          }
          groupsMap[permission.group].push(permission)
        }
      })

      return Object.keys(groupsMap).sort().map(groupKey => {
        const list = groupsMap[groupKey]
        const activeCount = list.filter(item => item.active).length

        return {
          key: groupKey,
          title: this.formatGroupTitle(groupKey),
          permissions: list,
          activeCount,
          total: list.length,
          allActive: activeCount === list.length && list.length > 0,
        }
      })
    },
  },
  watch: {
    search() {
      this.expandFiltered(false)
    },
    selectedGroup() {
      this.expandFiltered(false)
    },
  },
  created() {
    this.initialize()
  },
  methods: {
    ...mapActions('role', ['getPermission', 'storePermission', 'show']),
    ...mapMutations('login', ['setPermissions']),
    isPanelExpanded(key) {
      const panels = Array.isArray(this.expandedPanels)
        ? this.expandedPanels
        : [this.expandedPanels]

      return panels.includes(key)
    },
    initialize() {
      this.loading = true
      const roleId = this.$route.params.role_id

      Promise.all([
        this.getPermission(roleId),
        this.show(roleId),
      ])
        .then(([permissionResponse, roleResponse]) => {
          this.permissions = permissionResponse.data.permissions.map(item => ({ ...item }))
          this.permissions_role = permissionResponse.data.permissions_role || []
          this.role = roleResponse.data
          this.applyActiveFlags()
          this.expandFiltered()
        })
        .finally(() => {
          this.loading = false
        })
    },
    applyActiveFlags() {
      const activeIds = this.permissions_role.map(item => item.pivot.permission_id)
      this.permissions = this.permissions.map(item => ({
        ...item,
        active: activeIds.includes(item.id),
      }))
      this.originalPermissions = this.permissions.map(item => ({ ...item }))
      this.unsavedChanges = false
    },
    onTogglePermission(groupKey, permission, value) {
      permission.active = value
      this.ensurePanelExpanded(groupKey)
      this.markUnsaved()
    },
    onToggleGroup(group, value) {
      group.permissions.forEach(permission => {
        permission.active = value
      })
      this.ensurePanelExpanded(group.key)
      this.markUnsaved()
    },
    ensurePanelExpanded(key) {
      const panels = Array.isArray(this.expandedPanels)
        ? [...this.expandedPanels]
        : this.expandedPanels
          ? [this.expandedPanels]
          : []
      if (!panels.includes(key)) {
        panels.push(key)
        this.expandedPanels = panels
      }
    },
    markUnsaved() {
      this.unsavedChanges = true
      this.$nextTick(() => {
        this.expandFiltered()
      })
    },
    save() {
      if (this.saving || !this.unsavedChanges) return

      this.saving = true
      const data = {
        role_id: this.$route.params.role_id,
        permission: this.permissions,
      }

      this.storePermission(data)
        .then(() => {
          this.$toast.success('Permissões atualizadas com sucesso.')
          this.originalPermissions = this.permissions.map(item => ({ ...item }))
          this.unsavedChanges = false
          this.setPermissions(this.permissions)
          localStorage.setItem('permissions', JSON.stringify(this.permissions))
        })
        .finally(() => {
          this.saving = false
        })
    },
    resetChanges() {
      if (!this.unsavedChanges) return
      this.permissions = this.originalPermissions.map(item => ({ ...item }))
      this.unsavedChanges = false
      this.expandFiltered()
    },
    formatGroupTitle(group) {
      if (!group) return 'Outros'

      const normalized = group
        .toString()
        .replace(/_/g, ' ')

      return normalized
        .split(/\s+/)
        .map(word => {
          const [firstChar = '', ...rest] = word

          return firstChar.toLocaleUpperCase('pt-BR') + rest.join('').toLocaleLowerCase('pt-BR')
        })
        .join(' ')
    },
    expandFiltered(keepExisting = true) {
      this.$nextTick(() => {
        const groupKeys = this.filteredGroups.map(group => group.key)

        if (keepExisting) {
          const panels = Array.isArray(this.expandedPanels)
            ? [...this.expandedPanels]
            : this.expandedPanels
              ? [this.expandedPanels]
              : []
          const merged = Array.from(new Set([...panels, ...groupKeys]))
          this.expandedPanels = merged
        } else {
          this.expandedPanels = groupKeys
        }
      })
    },
    goBack() {
      this.$router.back()
    },
  },
}
</script>

<style scoped>
.permission-view {
  max-width: 1080px;
  margin: 0 auto;
  padding-bottom: 32px;
}

.permission-toolbar {
  margin-bottom: 24px;
  border-radius: 8px;
  background-color: #ffffff;
}

.permission-toolbar__info {
  display: flex;
  flex-direction: column;
}

.permission-toolbar__title {
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
}

.permission-toolbar__subtitle {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.filter-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-arrow {
  margin-left: 12px;
  transition: transform 0.2s ease;
}

.panel-actions__label {
  display: flex;
  align-items: center;
  transform: translateY(1px);
}

.panel-arrow--open {
  transform: rotate(180deg);
}

.filter-label {
  white-space: nowrap;
}

.filter-chip-group {
  flex: 1;
  min-width: 0;
}

.permission-panels .v-expansion-panel {
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.panel-header__title {
  font-weight: 600;
  font-size: 16px;
}

.panel-header__subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
}

.permission-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

.permission-item__title {
  font-weight: 500;
  margin-bottom: 2px;
}

.chip-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  border-radius: 10px;
  background: #1976d2;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

@media (max-width: 600px) {
  .permission-toolbar {
    flex-wrap: wrap;
    padding-bottom: 8px;
  }

  .permission-toolbar__subtitle {
    flex-wrap: wrap;
  }

  .permission-view {
    padding: 0 8px 24px;
  }

  .filter-controls {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .filter-chip-group {
    flex: initial;
    width: 100%;
  }
}
</style>
